import type { IconName } from '@dataroom/ui';
import type { ShortcutSpec } from '../keys';

/**
 * Scope kinds, outermost first (plan §4.2): `global < route < editor | grid | list < layer`. A key event
 * walks the active scopes from the innermost; the first command that matches and is enabled runs.
 */
export type ScopeKind = 'global' | 'route' | 'editor' | 'grid' | 'list' | 'layer';

export const SCOPE_LEVEL: Record<ScopeKind, number> = {
  global: 0,
  route: 1,
  editor: 2,
  grid: 2,
  list: 2,
  layer: 3,
};

/** A localized text, evaluated when shown (titles follow the member's language). */
export type Localized = () => string;

/**
 * A command scope. `Ctx` is what an active instance provides to its commands (the editor's history,
 * the list's selection …), so commands are typed against their scope.
 */
export type Scope<Ctx = unknown> = {
  readonly id: string;
  readonly kind: ScopeKind;
  readonly title: Localized;
  /**
   * Scope ids this scope can appear inside (narrows the conflict matrix: a list scope that only
   * exists on one route is never checked against another route's bindings). Empty = anywhere.
   */
  readonly within?: readonly string[];
  /** Phantom for `Ctx` inference; never set. */
  readonly __ctx?: (ctx: Ctx) => void;
};

export type CommandSource = 'keyboard' | 'menu' | 'palette' | 'api';

export type CommandRunInfo = {
  source: CommandSource;
  /** The key event when run from the keyboard. */
  event?: KeyboardEvent;
};

export type CommandDef<Ctx = unknown> = {
  /** Dotted, unique: `workflow.deleteStep`, `nav.inbox`. */
  readonly id: string;
  readonly title: Localized;
  readonly icon?: IconName;
  /** One binding or several (`['mod+shift+z', { windows: 'ctrl+y' }]`); omit for menu/palette-only commands. */
  readonly shortcut?: ShortcutSpec | readonly ShortcutSpec[];
  readonly scope: Scope<Ctx>;
  /** Enabled state; a disabled command lets the key fall through to outer scopes (and the browser). */
  readonly when?: (ctx: Ctx) => boolean;
  /**
   * Run while focus is in a text field. Defaults: false for single keys; for chords true in global,
   * route and layer scopes and false in editor, grid and list scopes (⌘Z, ⌘C, ⌘D keep their native
   * meaning in a form input).
   */
  readonly textSafe?: boolean;
  /**
   * Run a single key while focus is on a grid cell cursor although the scope is not the grid's (rule 1
   * of §4.2: letters edit the cell there). Never set this for a printable key.
   */
  readonly gridSafe?: boolean;
  /** Run on keyboard auto-repeat (nudges, list movement). */
  readonly repeat?: boolean;
  /** Command ids this command shadows on purpose; the conflict matrix accepts the duplicate. */
  readonly overrides?: readonly string[];
  /** Not listed in the shortcut sheet or the command bar. */
  readonly hidden?: boolean;
  /**
   * Listed in the shortcut sheet (muted, "needs a selection") while `when` is false but this is true (or `'always'`):
   * commands that wait for a selection stay discoverable instead of vanishing from the sheet. Omit to
   * list the command only while it is enabled.
   */
  readonly listed?: 'always' | ((ctx: Ctx) => boolean);
  /** Section inside its scope in the shortcut sheet (`Navigation`, `Selection` …). */
  readonly group?: Localized;
  /** Return `false` when the command did not apply: the key then falls through (no preventDefault). */
  readonly run: (ctx: Ctx, info: CommandRunInfo) => void | boolean | Promise<unknown>;
};

/** Variance-erased command for collections. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyCommand = CommandDef<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyScope = Scope<any>;

/** What a contribution file (`features/**\/commands.ts`) or a package registers. */
export type CommandContribution = {
  scopes?: readonly AnyScope[];
  commands?: readonly AnyCommand[];
};

export type ScopeActivation<Ctx> = {
  /** Context getter (read when a command runs; keep it cheap). */
  ctx: () => Ctx;
  /**
   * The scope's element. With `focus: 'within'` the scope is active only while focus (the key event's
   * target) is inside it; with `'mounted'` it is active while mounted and preferred when it contains
   * the target. `'within-or-idle'` is `'within'` that also stays active while nothing has focus (the
   * target is the document body, e.g. after the focused element was removed), so full-screen editors
   * keep their undo and zoom keys.
   */
  element?: () => Element | null;
  focus?: 'mounted' | 'within' | 'within-or-idle';
  /** The layer this instance lives in (set automatically by `useCommandScope` inside a layer). */
  layerId?: string | null;
};
